import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleSignInButton from './GoogleSignInButton';
import pendingActionService from '../services/pendingActionService';
import { trackLandingView, trackCTAClick, trackSignupStart, trackSignupComplete, trackLogin, trackAppInstallClick } from '../utils/analytics';
import { getPostAuthRedirect } from '../utils/authRedirect';
import SEO from './SEO';
import {
  PAGE_SEO,
  ORGANIZATION_JSONLD,
  WEBSITE_JSONLD,
  MOBILE_APP_JSONLD,
} from '../utils/seoConfig';
import './LandingPage.css';

// Sample menu images from the app's GCS bucket
const SAMPLE_MENUS = [
  {
    id: 'menu_001',
    title: 'Grilled Chicken Quinoa Bowl',
    image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_001.jpg',
    cuisine: 'Mediterranean',
    dietary: ['Gluten Free', 'Protein Rich']
  },
  {
    id: 'menu_002',
    title: 'Vegan Buddha Bowl',
    image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_002.jpg',
    cuisine: 'International',
    dietary: ['Vegan', 'Gluten Free']
  },
  {
    id: 'menu_004',
    title: 'Vegetarian Lentil Curry',
    image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_004.jpg',
    cuisine: 'Indian',
    dietary: ['Vegetarian', 'Gluten Free']
  },
  {
    id: 'menu_005',
    title: 'Keto Steak Salad',
    image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_005.jpg',
    cuisine: 'American',
    dietary: ['Keto', 'Low Carb']
  },
  {
    id: 'menu_006',
    title: 'Thai Green Curry',
    image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_006.jpg',
    cuisine: 'Thai',
    dietary: ['Vegetarian']
  },
  {
    id: 'menu_008',
    title: 'Korean BBQ Bowl',
    image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_008.jpg',
    cuisine: 'Korean',
    dietary: ['Protein Rich']
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // Rotate featured card every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % SAMPLE_MENUS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { trackLandingView('general'); }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    trackSignupStart('general');
    try {
      if (!credentialResponse.credential) {
        setAuthError('Missing authentication credentials');
        return;
      }

      const apiUrl = `${process.env.REACT_APP_API_URL}/auth/google`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          credential: credentialResponse.credential 
        }),
      });

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const data = await response.json();
      
      const userData = {
        ...data.user,
        sub: data.user.sub || data.user.id || data.user.email
      };
      
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);
      
      if (data.isNewUser) {
        trackSignupComplete('general', userData.sub || userData.email);
      } else {
        trackLogin('general', userData.sub || userData.email);
      }

      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      const next = getPostAuthRedirect({
        isNewUser: data.isNewUser,
        pendingRedirectUrl: redirectUrl,
        pendingSubscription: localStorage.getItem('pendingSubscription'),
        defaultHref: null
      });
      if (next) {
        if (redirectUrl) toast.success('Successfully connected with your coach!');
        window.location.href = next;
      } else {
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(`Login failed: ${error.message}`);
    }
  };

  const handleExploreClick = () => {
    trackCTAClick('general', 'hero');
    navigate('/menu');
  };

  const homeSeo = PAGE_SEO['/'];

  return (
    <div className="landing-page">
      <SEO
        title={homeSeo.title}
        description={homeSeo.description}
        keywords={homeSeo.keywords}
        canonical="/"
        jsonLd={[ORGANIZATION_JSONLD, WEBSITE_JSONLD, ...MOBILE_APP_JSONLD]}
      />
      {/* Decorative background elements */}
      <div className="landing-bg-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      {/* Hero Section - Centered text only */}
      <section className="landing-hero landing-hero-centered">
        <div className="hero-content hero-content-centered">
          <span className="hero-badge">Nutrition & Fitness Made Simple</span>
          <h1 className="hero-title">
            Your Kitchen,<br />
            <span className="highlight">Your Fitness</span>
          </h1>
          <p className="hero-subtitle">
            Discover hundreds of recipes, build personalized menus, follow workout programs, 
            and train with real coaches — nutrition and fitness, all in one place.
          </p>
          
          <div className="hero-cta hero-cta-centered">
            <div className="hero-cta-buttons">
              <button className="cta-primary" onClick={handleExploreClick}>
                <span>Explore Menus</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
              <button className="cta-secondary" onClick={() => { trackCTAClick('general', 'hero_secondary'); navigate('/fitness/programs'); }}>
                <span>Explore Fitness</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>

            <Link
              to="/pricing#pricing-personal"
              className="hero-pricing-link"
              onClick={() => trackCTAClick('general', 'hero_pricing')}
            >
              Free to start — plans from $5/month →
            </Link>

            <div className="cta-divider">
              <span>or</span>
            </div>

            <div className="login-section login-section-centered">
              <p className="login-label">Already a member?</p>
              {process.env.NODE_ENV === 'development' ? (
                <button 
                  onClick={() => navigate('/dev/login')}
                  className="dev-login-button"
                >
                  Dev Login
                </button>
              ) : (
                <GoogleSignInButton
                  onSuccess={handleGoogleSuccess}
                  onError={() => setAuthError('Login Failed')}
                  text="signin"
                  shape="pill"
                />
              )}
              {authError && <p className="auth-error">{authError}</p>}
            </div>
          </div>

          <div className="hero-app-download">
            <span className="hero-app-label">Meals &amp; Fitness on iOS &amp; Android</span>
            <div className="hero-app-badges">
              <a
                href="https://apps.apple.com/us/app/freshmenulab/id6758483247"
                className="app-store-badge"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download FreshFitLab on the App Store"
                onClick={() => trackAppInstallClick('ios', 'home_hero')}
              >
                <svg className="apple-logo" viewBox="0 0 384 512" width="20" height="20" fill="currentColor">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                <div className="app-store-badge-text">
                  <span className="badge-small">Download on the</span>
                  <span className="badge-large">App Store</span>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.freshmenulab"
                className="app-store-badge google-play-badge"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get FreshFitLab on Google Play"
                onClick={() => trackAppInstallClick('android', 'home_hero')}
              >
                <svg className="google-play-logo" viewBox="0 0 512 512" width="20" height="20" fill="currentColor">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                </svg>
                <div className="app-store-badge-text">
                  <span className="badge-small">Get it on</span>
                  <span className="badge-large">Google Play</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Showcase Section - Now separate and below hero */}
      <section className="landing-showcase">
        <div className="menu-showcase">
          <div className="showcase-header">
            <span className="showcase-count">500+</span>
            <span className="showcase-label">Recipes Ready to Cook</span>
          </div>
          <div className="showcase-grid">
            {SAMPLE_MENUS.map((menu, index) => (
              <Link
                key={menu.id}
                to={`/menu?search=${encodeURIComponent(menu.title)}`}
                className={`showcase-card ${index === activeCardIndex ? 'active' : ''}`}
                style={{ '--delay': `${index * 0.1}s` }}
                aria-label={`View ${menu.title} on the menu`}
                onClick={() => trackCTAClick('general', 'showcase_card')}
              >
                <img
                  src={menu.image}
                  alt={menu.title}
                  width="320"
                  height="240"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                />
                <div className="showcase-card-overlay">
                  <span className="showcase-card-title">{menu.title}</span>
                  <span className="showcase-card-cuisine">{menu.cuisine}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="showcase-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z"/>
            </svg>
            <span>Curated by food lovers</span>
          </div>
        </div>
      </section>

      {/* Fitness Showcase Section */}
      <section className="landing-showcase fitness-showcase">
        <div className="menu-showcase">
          <div className="showcase-header">
            <span className="showcase-count"><i className="fas fa-dumbbell"></i></span>
            <span className="showcase-label">Programs, Workouts & Exercises</span>
          </div>
          <div className="fitness-features-grid">
            <Link to="/fitness" className="fitness-feature-card" onClick={() => trackCTAClick('general', 'fitness_card_programs')}>
              <div className="fitness-feature-icon">
                <i className="fas fa-layer-group"></i>
              </div>
              <h3>Follow Programs</h3>
              <p>Multi-week training programs designed by coaches. Progressive overload built in.</p>
            </Link>
            <Link to="/fitness/workouts" className="fitness-feature-card" onClick={() => trackCTAClick('general', 'fitness_card_workouts')}>
              <div className="fitness-feature-icon">
                <i className="fas fa-clipboard-list"></i>
              </div>
              <h3>Browse Workouts</h3>
              <p>Hundreds of workouts for every goal — strength, cardio, mobility, and more.</p>
            </Link>
            <Link to="/fitness/exercises" className="fitness-feature-card" onClick={() => trackCTAClick('general', 'fitness_card_exercises')}>
              <div className="fitness-feature-icon">
                <i className="fas fa-running"></i>
              </div>
              <h3>Exercise Library</h3>
              <p>Detailed exercise guides with instructions, muscles targeted, and video demos.</p>
            </Link>
            <Link to="/fitness/my-workouts" className="fitness-feature-card" onClick={() => trackCTAClick('general', 'fitness_card_tracking')}>
              <div className="fitness-feature-icon">
                <i className="fas fa-calendar-check"></i>
              </div>
              <h3>Schedule & Track</h3>
              <p>Plan your training week and log every session. See your progress over time.</p>
            </Link>
          </div>
          <div className="showcase-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z"/>
            </svg>
            <span>Built for real training</span>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="landing-benefits">
        <h2 className="benefits-title">Everything You Need for Nutrition & Fitness</h2>
        
        <div className="benefits-grid benefits-grid-6">
          <Link to="/meal-planning" className="benefit-card benefit-card-link" onClick={() => trackCTAClick('general', 'benefit_meal_planning')}>
            <div className="benefit-icon benefit-icon-browse">
              <svg viewBox="0 0 48 48" fill="none">
                <rect x="6" y="6" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="3"/>
                <rect x="26" y="6" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="3"/>
                <rect x="6" y="26" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="3"/>
                <rect x="26" y="26" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="3"/>
                <circle cx="14" cy="14" r="4" fill="#4ade80"/>
                <circle cx="34" cy="14" r="4" fill="#f97316"/>
                <circle cx="14" cy="34" r="4" fill="#06b6d4"/>
                <circle cx="34" cy="34" r="4" fill="#a855f7"/>
              </svg>
            </div>
            <h3>Browse 500+ Recipes</h3>
            <p>Explore our curated collection spanning cuisines from around the world. Find your next favorite meal.</p>
          </Link>

          <div className="benefit-card">
            <div className="benefit-icon benefit-icon-plan">
              <svg viewBox="0 0 48 48" fill="none">
                <rect x="6" y="10" width="36" height="28" rx="4" stroke="currentColor" strokeWidth="3"/>
                <path d="M6 18h36" stroke="currentColor" strokeWidth="3"/>
                <rect x="12" y="24" width="8" height="8" rx="2" fill="#f97316"/>
                <rect x="24" y="24" width="8" height="8" rx="2" fill="#06b6d4"/>
                <path d="M14 12V6M34 12V6" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Build Your Menu</h3>
            <p>Plan your week with ease. Mix and match meals, and your shopping list builds automatically.</p>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon benefit-icon-shop">
              <svg viewBox="0 0 48 48" fill="none">
                <path d="M8 12h4l4 20h20l4-14H16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="20" cy="38" r="4" fill="#f97316" stroke="currentColor" strokeWidth="2"/>
                <circle cx="32" cy="38" r="4" fill="#f97316" stroke="currentColor" strokeWidth="2"/>
                <path d="M24 18v8M20 22h8" stroke="#4ade80" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Shop with Instacart</h3>
            <p>One click sends your grocery list to Instacart. Fresh ingredients delivered right to your door.</p>
          </div>

          <Link to="/fitness-training" className="benefit-card benefit-card-link" onClick={() => trackCTAClick('general', 'benefit_fitness')}>
            <div className="benefit-icon benefit-icon-fitness">
              <svg viewBox="0 0 48 48" fill="none">
                <path d="M8 24h4M36 24h4M12 18v12M36 18v12M12 24h24" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                <rect x="16" y="16" width="6" height="16" rx="2" fill="#a855f7" opacity="0.6"/>
                <rect x="26" y="16" width="6" height="16" rx="2" fill="#a855f7" opacity="0.6"/>
              </svg>
            </div>
            <h3>Track Your Workouts</h3>
            <p>Log exercises, follow structured programs, and watch your strength and endurance grow week by week.</p>
          </Link>

          <Link to="/coaching" className="benefit-card benefit-card-link" onClick={() => trackCTAClick('general', 'benefit_coaching')}>
            <div className="benefit-icon benefit-icon-coach">
              <svg viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="16" r="8" stroke="currentColor" strokeWidth="3"/>
                <path d="M10 40c0-7.7 6.3-14 14-14s14 6.3 14 14" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                <circle cx="36" cy="36" r="8" fill="#06b6d4"/>
                <path d="M33 36h6M36 33v6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Find a Coach</h3>
            <p>Connect with certified trainers who create personalized programs and nutrition plans just for you.</p>
          </Link>

          <div className="benefit-card">
            <div className="benefit-icon benefit-icon-nutrition">
              <svg viewBox="0 0 48 48" fill="none">
                <path d="M24 6c-6 0-12 8-12 18s6 18 12 18 12-8 12-18S30 6 24 6z" stroke="currentColor" strokeWidth="3"/>
                <path d="M24 6v36" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3"/>
                <circle cx="20" cy="20" r="3" fill="#4ade80"/>
                <circle cx="28" cy="28" r="3" fill="#f97316"/>
              </svg>
            </div>
            <h3>Nutrition Goals</h3>
            <p>Set calorie and macro targets, track your intake, and align your diet with your fitness goals.</p>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon benefit-icon-ai">
              <svg viewBox="0 0 48 48" fill="none">
                <rect x="8" y="14" width="32" height="24" rx="6" stroke="currentColor" strokeWidth="3"/>
                <path d="M16 24h8M16 30h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx="34" cy="10" r="6" fill="#a855f7"/>
                <path d="M31.5 10l1.5 1.5 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18 14V8M30 14V8" stroke="#4ade80" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>AI Coach</h3>
            <p>Get personalized guidance on nutrition and workouts — like having a knowledgeable training partner in your pocket, anytime you need one.</p>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon benefit-icon-support">
              <svg viewBox="0 0 48 48" fill="none">
                <path d="M8 24c0-8.8 7.2-16 16-16s16 7.2 16 16" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                <rect x="4" y="22" width="8" height="12" rx="4" stroke="currentColor" strokeWidth="3"/>
                <rect x="36" y="22" width="8" height="12" rx="4" stroke="currentColor" strokeWidth="3"/>
                <path d="M40 34c0 4.4-3.6 8-8 8h-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                <circle cx="24" cy="42" r="3" fill="#4ade80"/>
              </svg>
            </div>
            <h3>Dedicated Support</h3>
            <p>Paid subscribers get access to our support portal with ticketing, priority levels, and screenshot attachments.</p>
          </div>
        </div>
      </section>

      {/* Affiliate Section */}
      <section className="landing-affiliate">
        <div className="affiliate-content">
          <div className="affiliate-text">
            <span className="affiliate-badge">Creators & Coaches Welcome</span>
            <h2>Share Recipes or Train Clients</h2>
            <p>
              Whether you're a food blogger sharing recipes or a fitness coach building programs, 
              FreshFitLab gives you the tools to grow your audience and earn.
            </p>
            <ul className="affiliate-perks">
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Publish menus & workout programs</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Earn commission on Instacart orders</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Collect coaching payments via Stripe</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Manage clients with nutrition & fitness plans</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Track your earnings and analytics</span>
              </li>
            </ul>
            <div className="affiliate-cta-group">
              <Link to="/affiliate/register" className="affiliate-cta" onClick={() => trackCTAClick('general', 'affiliate_register')}>
                <span>Get Started as a Creator</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link to="/coaching" className="affiliate-cta affiliate-cta-secondary" onClick={() => trackCTAClick('general', 'coaching_landing')}>
                <span>Learn About Coaching</span>
              </Link>
            </div>
            <div className="landing-affiliate-pricing">
                <Link to="/pricing#pricing-creator" className="landing-affiliate-pricing-link">
                  View Pricing Plans
              </Link>
            </div>
          </div>
          <div className="affiliate-visual">
            <div className="earnings-card">
              <div className="earnings-header">
                <span className="earnings-label">Your Earnings</span>
                <span className="earnings-period">This Month</span>
              </div>
              <div className="earnings-amount">$247.50</div>
              <div className="earnings-chart">
                <div className="earnings-chart-bar" style={{ height: '40%' }}></div>
                <div className="earnings-chart-bar" style={{ height: '65%' }}></div>
                <div className="earnings-chart-bar" style={{ height: '50%' }}></div>
                <div className="earnings-chart-bar" style={{ height: '80%' }}></div>
                <div className="earnings-chart-bar active" style={{ height: '95%' }}></div>
              </div>
              <div className="landing-earnings-stats">
                <div className="landing-earnings-stat">
                  <span className="landing-stat-value">$180</span>
                  <span className="landing-stat-label">Coaching</span>
                </div>
                <div className="landing-earnings-stat">
                  <span className="landing-stat-value">$67</span>
                  <span className="landing-stat-label">Orders</span>
                </div>
                <div className="landing-earnings-stat">
                  <span className="landing-stat-value">8</span>
                  <span className="landing-stat-label">Clients</span>
                </div>
                <div className="landing-earnings-stat">
                  <span className="landing-stat-value">2.4k</span>
                  <span className="landing-stat-label">Views</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Section */}
      <section className="landing-business">
        <div className="business-content">
          <div className="business-text">
            <span className="business-badge">For Physical Therapy & Nutrition Businesses</span>
            <h2>Manage Your Practice,<br />All in One Place</h2>
            <p>
              Onboard your team, assign exercises and nutrition plans to clients, 
              and track progress — with enterprise-grade tools built for health & wellness businesses.
            </p>
            <ul className="business-perks">
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Business owner & employee roles</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Client onboarding via QR code, link, or invite</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Assign exercises & meal plans to clients</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>SSO integration for enterprise teams</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>Tiered plans: Starter, Professional, Enterprise</span>
              </li>
            </ul>
            <div className="business-cta-group">
              <Link to="/for-business" className="business-cta" onClick={() => trackCTAClick('general', 'business_landing')}>
                <span>Learn More for Business</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link to="/pricing#pricing-business" className="business-cta business-cta-secondary" onClick={() => trackCTAClick('general', 'business_pricing')}>
                <span>View Business Plans</span>
              </Link>
            </div>
          </div>

          <div className="business-visual">
            <div className="business-dashboard-card">
              <div className="biz-card-header">
                <span className="biz-card-title">Business Dashboard</span>
                <span className="biz-card-badge">Pro Plan</span>
              </div>
              <div className="biz-card-stats">
                <div className="biz-stat">
                  <span className="biz-stat-value">12</span>
                  <span className="biz-stat-label">Employees</span>
                </div>
                <div className="biz-stat">
                  <span className="biz-stat-value">48</span>
                  <span className="biz-stat-label">Clients</span>
                </div>
                <div className="biz-stat">
                  <span className="biz-stat-value">96%</span>
                  <span className="biz-stat-label">Active</span>
                </div>
              </div>
              <div className="biz-card-features">
                <div className="biz-feature-row">
                  <span className="biz-feature-dot active"></span>
                  <span>QR code client onboarding</span>
                </div>
                <div className="biz-feature-row">
                  <span className="biz-feature-dot active"></span>
                  <span>Exercise plan assignments</span>
                </div>
                <div className="biz-feature-row">
                  <span className="biz-feature-dot active"></span>
                  <span>Nutrition plan assignments</span>
                </div>
                <div className="biz-feature-row">
                  <span className="biz-feature-dot active"></span>
                  <span>Employee management</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Kitchen Section */}
      <section className="landing-community-kitchen">
        <div className="ck-landing-content">
          <div className="ck-landing-visual">
            <div className="ck-landing-cards">
              <div className="ck-landing-card ck-landing-card-recipient">
                <span className="ck-landing-card-icon">🍽️</span>
                <span className="ck-landing-card-role">Recipient</span>
                <span className="ck-landing-card-desc">Request a home-cooked meal</span>
              </div>
              <div className="ck-landing-card ck-landing-card-cook">
                <span className="ck-landing-card-icon">👨‍🍳</span>
                <span className="ck-landing-card-role">Cook</span>
                <span className="ck-landing-card-desc">Volunteer your kitchen skills</span>
              </div>
              <div className="ck-landing-card ck-landing-card-donor">
                <span className="ck-landing-card-icon">💝</span>
                <span className="ck-landing-card-role">Donor</span>
                <span className="ck-landing-card-desc">Fund ingredients for a family</span>
              </div>
            </div>
          </div>

          <div className="ck-landing-text">
            <span className="ck-landing-badge">Community Kitchen</span>
            <h2>Share a Meal,<br />Make a Difference</h2>
            <p>
              Community Kitchen connects people who need a meal with local volunteer cooks and
              generous donors — right in your neighborhood. Whether you cook, donate, or need support,
              we make it easy to help each other.
            </p>
            <div className="ck-landing-how">
              <div className="ck-landing-step">
                <span className="ck-landing-step-num">1</span>
                <div>
                  <strong>Sign up &amp; choose your role</strong>
                  <p>Be a recipient, cook, donor — or all three.</p>
                </div>
              </div>
              <div className="ck-landing-step">
                <span className="ck-landing-step-num">2</span>
                <div>
                  <strong>Get matched locally</strong>
                  <p>We pair you with people in your area based on needs and availability.</p>
                </div>
              </div>
              <div className="ck-landing-step">
                <span className="ck-landing-step-num">3</span>
                <div>
                  <strong>Cook, fund, or receive</strong>
                  <p>Pick recipes, order ingredients via Instacart, and share a meal.</p>
                </div>
              </div>
            </div>
            <Link to="/community-kitchen" className="ck-landing-cta">
              <span>Join Community Kitchen</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Get the App Section */}
      <section className="landing-get-app">
        <div className="get-app-content">
          <div className="get-app-text">
            <span className="get-app-badge-label">Now on iOS &amp; Android</span>
            <h2>Your Meals &amp; Workouts,<br />Always With You</h2>
            <p>
              Plan meals, browse recipes, follow workouts, and track your fitness progress — all from your phone. 
              Get the full FreshFitLab experience in a native app.
            </p>
            <ul className="get-app-features">
              <li>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                <span>Browse &amp; save recipes on the go</span>
              </li>
              <li>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                <span>Build meal plans from anywhere</span>
              </li>
              <li>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                <span>Follow workouts &amp; track progress</span>
              </li>
              <li>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                <span>One-tap Instacart ordering</span>
              </li>
            </ul>
            <div className="get-app-badges">
              <a
                href="https://apps.apple.com/us/app/freshmenulab/id6758483247"
                className="app-store-badge app-store-badge-lg"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download FreshFitLab on the App Store"
                onClick={() => trackAppInstallClick('ios', 'home_get_app')}
              >
                <svg className="apple-logo" viewBox="0 0 384 512" width="24" height="24" fill="currentColor">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                <div className="app-store-badge-text">
                  <span className="badge-small">Download on the</span>
                  <span className="badge-large">App Store</span>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.freshmenulab"
                className="app-store-badge app-store-badge-lg google-play-badge"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get FreshFitLab on Google Play"
                onClick={() => trackAppInstallClick('android', 'home_get_app')}
              >
                <svg className="google-play-logo" viewBox="0 0 512 512" width="24" height="24" fill="currentColor">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                </svg>
                <div className="app-store-badge-text">
                  <span className="badge-small">Get it on</span>
                  <span className="badge-large">Google Play</span>
                </div>
              </a>
            </div>
          </div>
          <div className="get-app-visual">
            <div className="phone-mockup">
              <div className="phone-screen">
                <img
                  src="/images/fresh_menu_lab_logo_1024_1024.jpeg"
                  alt="FreshFitLab App"
                  width="280"
                  height="280"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="phone-label">FreshFitLab</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="landing-final-cta">
        <div className="final-cta-content">
          <h2>Ready to level up your nutrition & fitness?</h2>
          <p>Join thousands who've made meal planning and training effortless.</p>
          <div className="final-cta-buttons">
            <button className="cta-primary cta-large" onClick={handleExploreClick}>
              <span>Get Started Free</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <a
              href="https://apps.apple.com/us/app/freshmenulab/id6758483247"
              className="app-store-badge app-store-badge-dark"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download FreshFitLab on the App Store"
              onClick={() => trackAppInstallClick('ios', 'home_final_cta')}
            >
              <svg className="apple-logo" viewBox="0 0 384 512" width="20" height="20" fill="currentColor">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              <div className="app-store-badge-text">
                <span className="badge-small">Download on the</span>
                <span className="badge-large">App Store</span>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.freshmenulab"
              className="app-store-badge app-store-badge-dark google-play-badge"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get FreshFitLab on Google Play"
              onClick={() => trackAppInstallClick('android', 'home_final_cta')}
            >
              <svg className="google-play-logo" viewBox="0 0 512 512" width="20" height="20" fill="currentColor">
                <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
              </svg>
              <div className="app-store-badge-text">
                <span className="badge-small">Get it on</span>
                <span className="badge-large">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
